import { useEffect, useMemo, useState } from 'react'
import {
  Checkbox,
  Grid,
  InputAdornment,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  LinearProgress,
  MenuItem,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks'
import { Exam, Site } from '../services/api'
import { doFindAcquisitionProtocols } from '../state/reducers/acqProtocolReducer'
import { debounce } from 'lodash'
import { updateExam } from '../state/thunks/procedureThunk'
import { enqueueSnackbar } from 'notistack'

interface ExamAquisitionProtocolProps {
  isEditMode: boolean
  onEditModeChange: (isEditing: boolean) => void
  onFileClick: (fileUrl: string) => void
  exam?: Exam
}

export function ExamAquisitionProtocol({
  isEditMode,
  onEditModeChange,
  onFileClick,
  exam,
}: ExamAquisitionProtocolProps) {
  const dispatch = useAppDispatch()

  const [selectedProtocolId, setSelectedProtocolId] = useState<number>(
    exam?.acquisitionProtocolId || -1,
  )
  const [isFetchingProtocols, setIsFetchingProtocols] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [selectedSiteId, setSelectedSiteId] = useState<number>(
    exam?.siteId || -1,
  )

  const aquisitionProtocols = useAppSelector(
    (state) => state.acProtocols.acquisitionProtocols,
  )

  const user = useAppSelector(({ auth }) => auth.user)

  const sites = useMemo(() => {
    return (user as any & { sites: Site[] })?.sites || []
  }, [user])

  const onFilterChange = useMemo(
    () =>
      debounce((value: string, siteId: number) => {
        setIsFetchingProtocols(true)
        dispatch(
          doFindAcquisitionProtocols({
            search: value,
            page: 0,
            limit: 50,
            siteIds: [siteId],
          }),
        ).finally(() => setIsFetchingProtocols(false))
      }, 300),
    [],
  )

  useEffect(() => {
    if (isEditMode) {
      onFilterChange(searchValue, selectedSiteId)
    }
  }, [searchValue, selectedSiteId, isEditMode])

  useEffect(() => {
    if (isEditMode && exam?.siteId) {
      setSelectedSiteId(exam.siteId)
    }
  }, [exam, isEditMode])

  async function handleSave() {
    setIsSaving(true)
    try {
      await dispatch(
        updateExam({
          id: exam?.id || -1,
          body: {
            acquisitionProtocolId: selectedProtocolId,
          },
        }),
      )

      enqueueSnackbar({
        message: 'Sauvegarde réussie',
        type: 'success',
      })
      onEditModeChange(false)
    } finally {
      setIsSaving(false)
    }
  }

  if (!isEditMode) {
    return (
      <div className="bg-white rounded-lg p-4 ">
        {exam?.acquisitionProtocol && (
          <div className="space-y-4">
            {exam.acquisitionProtocol && (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
                        Nom
                      </th>
                      <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
                        Description
                      </th>
                      <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
                        Document
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-sm">
                        {exam.acquisitionProtocol.title}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {exam.acquisitionProtocol.description}
                      </td>
                      {!!exam.acquisitionProtocol.pdfUrl && (
                        <td className="px-3 py-2 text-sm">
                          <Button
                            onClick={() =>
                              onFileClick(
                                `${process.env.REACT_APP_API_URL}/${exam.acquisitionProtocol?.pdfUrl}`,
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <i className="fas fa-file-pdf text-red-500 mr-1"></i>
                            Voir
                          </Button>
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg p-4 ">
      <h4 className="text-lg font-bold mb-2">Protocole de réalisation</h4>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Site"
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(Number(e.target.value))}
            fullWidth
            size="small"
          >
            {sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            label="Rechercher un protocole"
            placeholder="Nom du protocole..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i className="fas fa-search" />
                </InputAdornment>
              ),
            }}
          />
          <List className="mt-2  rounded-md h-[400px] overflow-y-scroll">
            {isFetchingProtocols ? (
              <LinearProgress color="secondary" />
            ) : (
              aquisitionProtocols.datas.map((protocol, index) => (
                <ListItem
                  key={protocol.id}
                  disablePadding
                  className={index % 2 === 0 ? 'bg-gray-50' : ''}
                >
                  <ListItemButton
                    onClick={() =>
                      setSelectedProtocolId(
                        selectedProtocolId === protocol.id ? -1 : protocol.id,
                      )
                    }
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedProtocolId === protocol.id}
                        edge="start"
                      />
                    </ListItemIcon>
                    <ListItemText primary={protocol.title} />
                    <ListItemSecondaryAction>
                      {!!protocol.pdfUrl && (
                        <IconButton
                          onClick={() =>
                            onFileClick(
                              `${process.env.REACT_APP_API_URL}/${protocol.pdfUrl}`,
                            )
                          }
                        >
                          <i className="fas fa-file-pdf text-red-500"></i>
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Grid>

        <Grid item xs={12}>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onEditModeChange(false)}
              size="small"
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              size="small"
            >
              {isSaving ? (
                <>
                  <CircularProgress size={16} className="mr-2" />
                  Enregistrement..
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

export default ExamAquisitionProtocol
